import React, { useState } from 'react';
import PropTypes from "prop-types";
import { useTranslation } from 'react-i18next';

const Questions = ({ questions, onHandleQuestionResponse, userResponse, language = 'en', ma = [] }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState(userResponse);

    // 'hi' (India) and 'jp' (Japan) both read the localized `hindi` field,
    // which the backend `field:`-aliases to the country's localization column.
    // Fall back to the English field when the localized value is missing.
    const localize = (item, enField) => {
        const isLocalized = language === 'hi' || language === 'jp';
        return isLocalized ? (item.hindi || item[enField]) : item[enField];
    };

    React.useEffect(() => {
        if (userResponse && typeof userResponse === 'object' && Object.keys(userResponse).length > 0) {
            setFormData(userResponse);
        }
    }, [userResponse]);

    const handleChange = (questionId, value) => {
        setFormData((prevData) => {
            const updatedData = {
                ...prevData,
                [questionId]: value,
            };

            onHandleQuestionResponse(updatedData);
            return updatedData;
        });
    };

    const renderQuestion = (question) => {
        switch (question.displayType) {
            case 1:
                // Dropdown
                return (
                    <div className="jumbotron bg-white p-3 border shadow-sm" key={question.id}>
                        <div key={question.id}>
                            <label>{localize(question, 'text')}</label>
                            <select
                                style={{
                                    width: '100%',
                                    display: 'block',
                                    height: '40px',
                                    lineHeight: '1.5',
                                    color: '#495057',
                                    backgroundColor: '#fff',
                                    backgroundClip: 'padding-box',
                                    border: '1px solid #ced4da',
                                    borderRadius: '5px',
                                    transition:
                                        'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                }}
                                name='blackListType'
                                id='type'
                                required
                                value={formData[question.id] || ''}
                                onChange={(e) => handleChange(question.id, e.target.value)}
                            >
                                <option value="">{t('panelistProfile.questions.selectOptionPlaceholder')}</option>
                                {renderOptions(question.options)}
                            </select>
                        </div>
                    </div>
                );

            case 2:
                // Radio buttons
                return (
                    <div className="jumbotron bg-white p-3 border shadow-sm" key={question.id}>
                        <div key={question.id}>
                            <label>{localize(question, 'text')}</label>
                            <div className="custom-control custom-radio">
                                {question.options.map((option) => (
                                    <div key={option.id}>
                                        <input
                                            type="radio"
                                            className="custom-control-input"
                                            id={option.id}
                                            name={question.id}
                                            value={option.id}
                                            checked={formData[question.id] === option.id}
                                            onChange={() => handleChange(question.id, option.id)}
                                        />
                                        <label htmlFor={option.id} className="custom-control-label">{localize(option, 'value')}</label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 4:
                // Checkboxes
                return (
                    <div className="jumbotron bg-white p-3 border shadow-sm" key={question.id}>
                        <div key={question.id}>
                            <label>{localize(question, 'text')}</label>
                            {question.options.map((option) => (
                                <div key={option.id} className="col ml-2">
                                    <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={option.id}
                                        value={option.id}
                                        checked={formData[question.id]?.includes(option.id) || false}
                                        onChange={() => handleCheckboxChange(question.id, option.id)}
                                    />
                                    <label className="custom-control-label" htmlFor={option.id}>{localize(option, 'value')}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            // Add more cases for other display types as needed

            default:
                return null;
        }
    };

    const renderOptions = (options) => {
        return options.map((option) => (
            <option key={option.id} value={option.id}>
                {localize(option, 'value')}
            </option>
        ));
    };
    //NEW
    const safeNoneOfAboveIds = Array.isArray(ma) ? ma : [];
    const handleCheckboxChange = (questionId, value) => {
        setFormData((prevData) => {
            let updatedValues;
            if (safeNoneOfAboveIds.includes(value)) {
                updatedValues = [value];
            } else {
                const currentValues = prevData[questionId] || [];
                updatedValues = currentValues.includes(value)
                    ? currentValues.filter((v) => v !== value)
                    : [...currentValues, value];
            }
            // Remove None of above IDs if any other option is selected
            if (updatedValues.length > 0 && !safeNoneOfAboveIds.includes(value)) {
                updatedValues = updatedValues.filter(id => !safeNoneOfAboveIds.includes(id));
            }

            onHandleQuestionResponse({
                ...prevData,
                [questionId]: updatedValues,
            });
            return {
                ...prevData,
                [questionId]: updatedValues,
            };
        });
    };

    return (
        <div>
            {questions.map((question) => renderQuestion(question))}
        </div>
    );
};

Questions.propTypes = {
    questions: PropTypes.array.isRequired,
    onHandleQuestionResponse: PropTypes.func.isRequired,
    userResponse: PropTypes.object,
    language: PropTypes.string,
    ma: PropTypes.array
}

export default Questions;
